import { Button, Select } from '../../../design-system';

// Writing a note — the same box on the client's Notes tab and on the Notes
// screen, differing only in whether it offers a client picker.
//
// ⚠️ A TOP-LEVEL COMPONENT, NOT ONE DECLARED INSIDE ITS PARENT. Both callers
// used to define this inline in their render, which makes it a NEW component
// type on every keystroke: React unmounts the old tree and mounts a fresh one,
// the textarea loses focus after each character, and the caret jumps to the
// end. Keep it out here.

// The two scopes, in the user's own words. "Shared" stops at the organisation —
// there is no audience beyond the firm anywhere in this feature.
export const VISIBILITY = [
  { value: 'shared', label: 'Shared — everyone in the firm with notes access' },
  { value: 'personal', label: 'Personal — only me (and anyone I tag)' },
];

export const emptyDraft = {
  body: '',
  visibility: 'shared',
  taggedUserIds: [],
  isPinned: false,
  // '' means "no client" — a general note. Kept as a string because it is a
  // <select> value; the server reads a falsy one as null.
  clientId: '',
};

function TagPicker({ orgUsers, value, onToggle }) {
  return (
    <div className="cn-tagpicker">
      <span className="cn-tagpicker-label">Tag colleagues</span>
      <div className="cn-tagpicker-list">
        {orgUsers.length === 0 && <span className="cn-muted">No colleagues to tag yet.</span>}
        {orgUsers.map((u) => {
          const on = value.includes(u._id);
          return (
            <button
              type="button"
              key={u._id}
              className={`cn-tagchip ${on ? 'is-on' : ''}`}
              onClick={() => onToggle(u._id)}
              aria-pressed={on}
            >
              {u.name || u.email}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default function NoteComposer({
  value,
  onChange,
  onSubmit,
  onCancel,
  submitLabel = 'Add note',
  saving = false,
  orgUsers = [],
  // Pass a client list to offer the picker; omit it where the client is
  // already decided by the screen you are on (the client's own Notes tab).
  clients = null,
  placeholder = 'Write it down. Nobody outside the firm can read this.',
}) {
  const set = (patch) => onChange((d) => ({ ...d, ...patch }));
  const toggleTag = (id) => onChange((d) => ({
    ...d,
    taggedUserIds: d.taggedUserIds.includes(id)
      ? d.taggedUserIds.filter((x) => x !== id)
      : [...d.taggedUserIds, id],
  }));

  return (
    <div className="cn-composer">
      <textarea
        className="cn-textarea"
        rows={4}
        placeholder={placeholder}
        value={value.body}
        onChange={(e) => set({ body: e.target.value })}
      />

      {clients && (
        <Select
          label="Client"
          className="cn-composer-client"
          value={value.clientId || ''}
          onChange={(e) => set({ clientId: e.target.value })}
          helperText="Leave this empty for a note that isn't about any client."
        >
          <option value="">No client — general note</option>
          {clients.map((c) => (
            <option key={c._id} value={c._id}>
              {c.displayName}{c.clientCode ? ` · ${c.clientCode}` : ''}
            </option>
          ))}
        </Select>
      )}

      <TagPicker orgUsers={orgUsers} value={value.taggedUserIds} onToggle={toggleTag} />

      <div className="cn-composer-actions">
        <Select
          label="Who can see it"
          value={value.visibility}
          onChange={(e) => set({ visibility: e.target.value })}
        >
          {VISIBILITY.map((v) => <option key={v.value} value={v.value}>{v.label}</option>)}
        </Select>
        <label className="cn-check">
          <input
            type="checkbox"
            checked={value.isPinned}
            onChange={(e) => set({ isPinned: e.target.checked })}
          />
          Pin to the top
        </label>
        <span style={{ flex: 1 }} />
        {onCancel && <Button type="button" variant="secondary" size="sm" onClick={onCancel}>Cancel</Button>}
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={onSubmit}
          loading={saving}
          disabled={!value.body.trim()}
        >
          {submitLabel}
        </Button>
      </div>

      {value.visibility === 'personal' && (
        <p className="cn-note-hint">
          Only you and anyone you tag will see this. Nobody else in the firm can open it — not
          colleagues with notes access, not an Admin.
        </p>
      )}
    </div>
  );
}
